import { useId, type ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import MuiCheckbox, { type CheckboxProps } from '@mui/material/Checkbox';
import MuiFormControl, { type FormControlProps } from '@mui/material/FormControl';
import MuiFormControlLabel, { type FormControlLabelProps } from '@mui/material/FormControlLabel';
import MuiFormHelperText, { type FormHelperTextProps } from '@mui/material/FormHelperText';
import MuiMenuItem, { type MenuItemProps } from '@mui/material/MenuItem';
import MuiSelect, { type SelectProps as MuiSelectProps } from '@mui/material/Select';
import MuiInputAdornment from '@mui/material/InputAdornment';
import MuiTextField, { type TextFieldProps as MuiTextFieldProps } from '@mui/material/TextField';
import type { SxProps, Theme } from '@mui/material/styles';
import Box from '../primitives/Box';
import {
  characterCountLabelSx,
  fieldLabelSx,
  formControlSx,
  outlinedFieldSx,
  outlinedSelectCompactSx,
  selectPlaceholderSx,
  selectMenuItemSx,
  selectMenuPaperSx,
} from '../styles/formControlStyles';
import { mergeSx } from '../utils/mergeSx';

export type { FormControlProps };

/** `default` is the theme field (36px). `compact` is 32px for toolbars. */
export type SelectDensity = 'default' | 'compact';

export type SelectProps<Value = unknown> = MuiSelectProps<Value> & {
  density?: SelectDensity;
};

export type TextFieldProps = Omit<MuiTextFieldProps, 'InputLabelProps'> & {
  /** Rendered on the right of the external label, same row. */
  labelEnd?: ReactNode;
};
export type { CheckboxProps };
export type { FormControlLabelProps };
export type { MenuItemProps };
export type { FormHelperTextProps };

interface FieldLabelProps {
  children: ReactNode;
  required?: boolean;
  htmlFor?: string;
  id?: string;
  sx?: SxProps<Theme>;
  /** MUI InputLabel leftover. External labels sit above the field, so this is ignored. */
  shrink?: boolean;
  /** MUI InputLabel leftover. External labels do not use the error color. */
  error?: boolean;
}

const FieldLabelRoot = styled('label')({});

const FieldLabel = ({ children, required, htmlFor, id, sx }: FieldLabelProps) => (
  <FieldLabelRoot id={id} htmlFor={htmlFor} sx={mergeSx(fieldLabelSx, sx)}>
    {children}
    {required ? ' *' : null}
  </FieldLabelRoot>
);

export type InputLabelProps = FieldLabelProps;

const InputLabel = (props: InputLabelProps) => <FieldLabel {...props} />;

export { default as InputAdornment } from '@mui/material/InputAdornment';
export type { InputAdornmentProps } from '@mui/material/InputAdornment';

const mergeMenuPaperSx = (MenuProps?: SelectProps['MenuProps']): SxProps<Theme> => {
  const paperSlot = MenuProps?.slotProps?.paper;
  const paperSlotSx =
    paperSlot && typeof paperSlot === 'object' && 'sx' in paperSlot ? paperSlot.sx : undefined;

  return mergeSx(selectMenuPaperSx, paperSlotSx);
};

function Select<Value = string>({
  MenuProps,
  label: _label,
  density = 'default',
  sx,
  ...props
}: SelectProps<Value>) {
  const paperSlot = MenuProps?.slotProps?.paper;
  const paperSlotObject = paperSlot && typeof paperSlot === 'object' ? paperSlot : undefined;
  const paperSx = mergeMenuPaperSx(MenuProps);

  return (
    <MuiSelect<Value>
      sx={mergeSx(density === 'compact' ? outlinedSelectCompactSx : undefined, sx)}
      MenuProps={{
        ...MenuProps,
        slotProps: {
          ...MenuProps?.slotProps,
          paper: {
            ...paperSlotObject,
            sx: paperSx,
          },
        },
      }}
      {...props}
    />
  );
}

const MenuItem = ({ sx, ...props }: MenuItemProps) => (
  <MuiMenuItem sx={mergeSx(selectMenuItemSx, sx)} {...props} />
);

const FormControl = ({ size = 'small', sx, ...props }: FormControlProps) => (
  <MuiFormControl
    size={size}
    sx={mergeSx(formControlSx, mergeSx(outlinedFieldSx, sx))}
    {...props}
  />
);

const FormHelperText = (props: FormHelperTextProps) => <MuiFormHelperText {...props} />;

/** Matches MuiFormControl marginNormal / marginDense when label is rendered outside the input. */
const labeledTextFieldMarginSx = (
  margin: TextFieldProps['margin'],
): SxProps<Theme> | undefined => {
  if (margin === 'normal') return { mt: 2, mb: 1 };
  if (margin === 'dense') return { mt: 1, mb: 0.5 };
  return undefined;
};

const TextField = ({
  size = 'small',
  sx,
  label,
  labelEnd,
  required,
  slotProps,
  id,
  margin,
  ...props
}: TextFieldProps) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const { inputLabel: _inputLabel, ...restSlotProps } = slotProps ?? {};

  const input = (
    <MuiTextField
      id={inputId}
      size={size}
      sx={mergeSx(outlinedFieldSx, sx)}
      required={required}
      margin={label == null ? margin : 'none'}
      slotProps={restSlotProps}
      {...props}
    />
  );

  if (label == null) {
    return input;
  }

  return (
    <Box
      sx={mergeSx(
        {
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          width: props.fullWidth ? '100%' : undefined,
        },
        labeledTextFieldMarginSx(margin),
      )}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 1,
          minWidth: 0,
        }}
      >
        <FieldLabel htmlFor={inputId} required={required} sx={{ flex: '1 1 auto', minWidth: 0 }}>
          {label}
        </FieldLabel>
        {labelEnd}
      </Box>
      {input}
    </Box>
  );
};

const Checkbox = ({ size = 'small', sx, ...props }: CheckboxProps) => (
  <MuiCheckbox size={size} sx={sx} {...props} />
);

const FormControlLabel = ({ sx, ...props }: FormControlLabelProps) => (
  <MuiFormControlLabel sx={mergeSx({ m: 0 }, sx)} {...props} />
);

/** Empty Select display — same color/opacity as TextField `::placeholder`. */
const SelectPlaceholder = ({ children }: { children: ReactNode }) => (
  <Box component="span" sx={selectPlaceholderSx}>
    {children}
  </Box>
);

/**
 * Live `current / max`, same color as the placeholder.
 * Labeled fields use `placement="label"` via `TextField` `labelEnd`.
 * Fields without an external label (table cells) use `adornment` inside the input.
 */
const CharacterCount = ({
  current,
  max,
  multiline = false,
  placement = 'label',
}: {
  current: number;
  max: number;
  /** Sit at the bottom-right of a textarea instead of vertically centered. */
  multiline?: boolean;
  placement?: 'adornment' | 'label';
}) => {
  if (placement === 'label') {
    return (
      <Box component="span" sx={characterCountLabelSx}>
        {`${current} / ${max}`}
      </Box>
    );
  }
  const count = (
    <Box component="span" sx={mergeSx(selectPlaceholderSx, { whiteSpace: 'nowrap', flexShrink: 0 })}>
      {`${current} / ${max}`}
    </Box>
  );
  return (
    <MuiInputAdornment position="end" sx={multiline ? { alignSelf: 'flex-end', mr: 0 } : { mr: 0 }}>
      {count}
    </MuiInputAdornment>
  );
};

export {
  CharacterCount,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectPlaceholder,
  TextField,
};
